"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { useNavGuardStore } from "~/store/navGuardStore";

/**
 * Intercepts clicks on in-app `<a>` links (NavBar, footer, etc.) while a
 * listing surface has unsaved edits, and hands the navigation to that surface's
 * own `attemptNavigation` — which shows the unsaved-changes prompt and only
 * proceeds once the user picks Save / Discard. Mounted once in the root layout,
 * above the NavBar, so it can guard NavBar links without the NavBar/UserMenu
 * components knowing anything about unsaved state.
 *
 * We delegate (rather than push the route here) so each surface keeps its own
 * history/trap handling — pushing the route from here would race the edit
 * provider's back-button trap and bounce the user back to the page.
 *
 * Scope notes:
 * - Only same-origin, in-app navigations are guarded. New-tab / modified
 *   clicks, downloads, and same-page links are ignored.
 * - Anchors inside `[data-skip-global-nav-guard]` (the listing detail shell)
 *   are skipped so the in-section nav keeps its own trap-aware guard and we
 *   never double-prompt.
 * - Imperative navigations (`router.push` from the UserMenu items and the
 *   desktop context-toggle CTA) aren't `<a href>` clicks, so this listener
 *   never sees them. Those call sites consult the same guard directly via
 *   `useGuardedNavigate`. Note `beforeunload` does NOT cover these — it only
 *   fires on reload/close and the sign-out `window.location.href`, not on
 *   same-document SPA transitions.
 */
export function GlobalNavGuard() {
  const router = useRouter();
  const attemptNavigation = useNavGuardStore((s) => s.attemptNavigation);

  useEffect(() => {
    if (!attemptNavigation) return;

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      // Let the browser handle new-tab / modified / non-primary-button clicks.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }

      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      // Surfaces that run their own (trap-aware) navigation guard opt out.
      if (anchor.closest("[data-skip-global-nav-guard]")) return;

      const href = anchor.getAttribute("href");
      if (!href) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Same page (path + query) — nothing to leave.
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        return;
      }

      // Block the browser default and stop the event before it can reach
      // Next's <Link> click handler (React root, bubble phase) so the
      // navigation can't slip past, then route it through the dirty surface's
      // prompt. We run in the capture phase, so stopPropagation() already
      // prevents the event from reaching the target/bubble phases — no need for
      // stopImmediatePropagation(), which would also suppress unrelated
      // document listeners (analytics, outside-click) on guarded clicks.
      e.preventDefault();
      e.stopPropagation();
      const dest = url.pathname + url.search + url.hash;
      attemptNavigation(() => router.push(dest));
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [attemptNavigation, router]);

  return null;
}
